// Share trigger for Letter Flick.
//
// Tries navigator.share first (native iOS share sheet, Android share intent),
// falls back to navigator.clipboard.writeText, and finally reveals an inline
// <pre> block in the modal so users on insecure contexts (http:// over a LAN
// IP, where both navigator.share and clipboard are gated) can long-press →
// Select All → Copy from the iOS context menu.
//
// AbortError handling: when the user opens the native share sheet and dismisses
// it without picking a target, navigator.share rejects with `AbortError`. That
// is NOT a failure — the player simply cancelled — so we return a distinct
// `cancelled` outcome.

export type ShareOutcome =
  | 'shared'
  | 'copied'
  | 'cancelled'
  | 'failed'

// navigator.share availability check: the Web Share API has historically been
// gated to secure contexts and to user-gesture handlers. We feature-detect
// rather than UA-sniff. The runtime call is also try/catch-guarded since some
// browsers expose navigator.share but reject for non-shareable payloads
// (uncommon for plain text, but defensive).
function canNativeShare(payload: { text: string; url?: string }): boolean {
  if (typeof navigator.share !== 'function') return false
  if (typeof navigator.canShare === 'function') {
    try {
      return navigator.canShare(payload)
    } catch {
      return false
    }
  }
  return true
}

function isAbortError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false
  const name = (err as { name?: unknown }).name
  return typeof name === 'string' && name === 'AbortError'
}

// Attempt to share `text`. Returns the outcome so callers can branch (none do
// today; the inline-text reveal is the only user-visible side effect for the
// fallback path).
//
// `fallbackEl` is an optional element that receives the share text when both
// APIs fail. Hidden by default; revealed on failure. Hidden again on success
// or cancel so a previous failure's text doesn't linger.
export async function triggerShare(
  text: string,
  fallbackEl: HTMLElement | null = null,
  url?: string,
): Promise<ShareOutcome> {
  // Passing `url` (alongside text) is what flips the iOS share sheet from
  // a generic "text content" card (the A| placeholder icon) to a proper
  // link-preview card that uses the page's apple-touch-icon as the small
  // thumbnail and the og:image as the large preview. Text-only shares get
  // the iOS "plain text" treatment regardless of whether a URL is embedded
  // inside the text body.
  //
  // sharegrid.ts produces a fully self-contained share string with the URL
  // appended on its own line — that's the canonical "what the user shares"
  // and is what every fallback path uses. For the native-share payload we
  // strip the URL itself (it's sent as the separate `url` field; leaving it in
  // both produces a duplicate URL in iOS' Copy, which concatenates text + url)
  // but KEEP the newline that preceded it, so iOS' join lands the link on its
  // own line instead of space-gluing it onto the grid.
  const payload: { text: string; url?: string } =
    url !== undefined ? { text: stripTrailingUrl(text, url), url } : { text }
  if (canNativeShare(payload)) {
    try {
      await navigator.share(payload)
      hideFallback(fallbackEl)
      return 'shared'
    } catch (err) {
      if (isAbortError(err)) {
        hideFallback(fallbackEl)
        return 'cancelled'
      }
    }
  }

  if (
    typeof navigator.clipboard !== 'undefined' &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(text)
      hideFallback(fallbackEl)
      return 'copied'
    } catch {
      // Fall through to inline-text fallback.
    }
  }

  if (fallbackEl !== null) {
    fallbackEl.textContent = text
    fallbackEl.hidden = false
    fallbackEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
  return 'failed'
}

function stripTrailingUrl(text: string, url: string): string {
  // Strip the URL but KEEP the newline that preceded it. The native-share
  // payload sends the URL as a separate field, and iOS' Copy joins the text
  // and url fields; keeping the trailing "\n" makes that join land the link on
  // its own line (`…✅\n` + url) instead of space-gluing it onto the grid.
  // Defensive: only fire on the exact `\n<url>` suffix.
  const suffix = `\n${url}`
  return text.endsWith(suffix) ? text.slice(0, -url.length) : text
}

function hideFallback(el: HTMLElement | null): void {
  if (el === null) return
  el.hidden = true
  el.textContent = ''
}
